import * as XLSX from 'xlsx';

// Rutas a tus funciones Serverless en Netlify (api/)
const API_ALMACEN = "/api/almacen"; 
const API_FECHA_CORTA = "/api/fechacorta";

/**
 * Obtiene el inventario general completo desde el backend
 * @returns {Promise<Array>} Lista de productos en inventario
 */
export const getInventarioGeneral = async () => {
  try {
    const response = await fetch(API_ALMACEN, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      throw new Error(`Error al consultar el inventario general (Código: ${response.status})`);
    }

    return await response.json();
  } catch (error) {
    throw new Error(error.message);
  }
};

/**
 * Obtiene los productos que están a 6 meses o menos de vencer
 * @returns {Promise<Array>} Lista de productos próximos a vencer
 */
export const getInventarioFechaCorta = async () => {
  try {
    const response = await fetch(API_FECHA_CORTA, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      throw new Error(`Error al consultar productos de fecha corta (Código: ${response.status})`);
    }

    return await response.json();
  } catch (error) {
    throw new Error(error.message);
  }
};

/**
 * Genera y descarga un archivo Excel parametrizado para cualquier reporte de inventario
 * @param {Array} productos - Registros devueltos por la API
 * @param {String} tipoReporte - Nombre descriptivo para el archivo ('General' o 'Fecha_Corta')
 */
export const exportarInventarioExcel = (productos, tipoReporte = "General") => {
  try {
    if (!productos || productos.length === 0) {
      throw new Error("No hay productos en el listado para exportar.");
    }

    let inversionTotalAlmacen = 0;
    let valorVentaTotalAlmacen = 0;

    // 1. Mapeamos y calculamos las columnas del reporte
    const datosFormateados = productos.map((p) => {
      const stock = Number(p.stock || 0);
      const pCompra = Number(p.precio_compra || 0);
      const pVenta = Number(p.precio_venta || 0);

      // Cálculos por cada fila de producto
      const totalInversionFila = stock * pCompra;
      const totalVentaFila = stock * pVenta;

      // Acumuladores globales
      inversionTotalAlmacen += totalInversionFila;
      valorVentaTotalAlmacen += totalVentaFila;

      // Estructura base del JSON para la hoja
      const fila = {
        "Código": `#${p.productos_id}`,
        "Nombre del Producto": p.nombre,
        "Categoría": p.categoria,
        "Stock Actual": stock,
        "Stock Mínimo": Number(p.stock_min || 0),
        "Precio Compra": `S/ ${pCompra.toFixed(2)}`,
        "Precio Venta": `S/ ${pVenta.toFixed(2)}`,
        "Total Invertido": `S/ ${totalInversionFila.toFixed(2)}`,
        "Valor Total Venta": `S/ ${totalVentaFila.toFixed(2)}`,
        "Fecha de Vencimiento": p.fecha_vencimiento || 'N/A'
      };

      // Si el backend incluye los días restantes (como en fecha corta), lo agregamos al Excel
      if (p.dias_para_vencer !== undefined) {
        fila["Días Restantes"] = `${p.dias_para_vencer} días`;
      }

      return fila;
    });

    // 2. Fila de separación limpia
    const filaVacia = {
      "Código": "", "Nombre del Producto": "", "Categoría": "", "Stock Actual": "", "Stock Mínimo": "",
      "Precio Compra": "", "Precio Venta": "", "Total Invertido": "", "Valor Total Venta": "", "Fecha de Vencimiento": ""
    };
    if (productos[0]?.dias_para_vencer !== undefined) filaVacia["Días Restantes"] = "";
    datosFormateados.push(filaVacia);

    // 3. Fila de Balance y Resumen Totalizador del Capital
    const filaResumen = {
      "Código": "RESUMEN GENERAL",
      "Nombre del Producto": `Total de Variedad: ${productos.length} ítems`,
      "Categoría": "",
      "Stock Actual": "",
      "Stock Mínimo": "",
      "Precio Compra": "",
      "Precio Venta": "TOTALES:",
      "Total Invertido": `S/ ${inversionTotalAlmacen.toFixed(2)}`,
      "Valor Total Venta": `S/ ${valorVentaTotalAlmacen.toFixed(2)}`,
      "Fecha de Vencimiento": ""
    };
    if (productos[0]?.dias_para_vencer !== undefined) filaResumen["Días Restantes"] = "";
    datosFormateados.push(filaResumen);

    // 4. Transformar JSON a formato de hoja
    const hoja = XLSX.utils.json_to_sheet(datosFormateados);
    const libro = XLSX.utils.book_new();
    
    XLSX.utils.book_append_sheet(libro, hoja, `Reporte ${tipoReporte}`);

    // 5. Descarga automática del archivo con la fecha de hoy
    const fechaHoy = new Date().toISOString().split('T')[0];
    const nombreArchivo = `Inventario_${tipoReporte}_${fechaHoy}.xlsx`;
    
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    if (isIOS) {
        XLSX.writeFile(libro, nombreArchivo);
    } else {
        const excelBuffer = XLSX.write(libro, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = nombreArchivo;
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        setTimeout(() => URL.revokeObjectURL(url), 250); 
    }

  } catch (error) {
    throw new Error("Error al generar el Excel de inventario: " + error.message);
  }
};